import Vue from 'vue'
import uniqid from 'uniqid';
import jwtDecode from 'jwt-decode';
import { fromUnixTime, differenceInMinutes, isPast } from 'date-fns'
import store from '@/store'

import _ from 'lodash';
import router from '@/router';
import { useAuthStore, useSetupStore } from '@/stores';
import Auth from '@/store/api/system/auth';

// Billing: the platform's markup on top of the provider's actual cost for
// metered rows. The API sends `margin_multiplier: null` BOTH when no per-row
// margin is configured and for every non-super-user, and applies this default
// itself (config/billing.php -> provider_cost.default_margin_multiplier,
// BillingFeatureCost::effectiveMarginMultiplier). Resolve it in one place so
// the billing screens cannot disagree about what "no margin set" means.
export const DEFAULT_PROVIDER_MARGIN_MULTIPLIER = 2;

export const resolveMarginMultiplier = (marginMultiplier) => {
    if (marginMultiplier === null || marginMultiplier === undefined) {
        return DEFAULT_PROVIDER_MARGIN_MULTIPLIER;
    }

    const parsed = parseFloat(marginMultiplier);

    return Number.isFinite(parsed) ? parsed : DEFAULT_PROVIDER_MARGIN_MULTIPLIER;
}

// Multistore: data that lives on the shared shop belongs to the OWNER store.
// Writing it requires the right THERE, which the API resolves per caller into
// `shop.can_manage_shared_data`. Screens showing such data are flagged with
// `sharedWithShop` route meta, so per-store screens (payment methods, shipping
// methods, POS …) keep their normal permissions.
const WRITE_PERMISSION_TYPES = ['create', 'update', 'delete'];

const isSharedShopDataWriteBlocked = (type) => {
    if (!WRITE_PERMISSION_TYPES.includes(type)) return false;

    const currentRoute = _.get(router, 'currentRoute');
    if (!_.get(currentRoute, 'meta.sharedWithShop')) return false;

    const activeCms = useSetupStore().activeCms;
    return _.get(activeCms, 'shop.can_manage_shared_data') === false;
}

export const checkRestrictions = (permissions, type = 'access') => {
    const authStore = useAuthStore();
    let allowByPermission = true;

    if (isSharedShopDataWriteBlocked(type)) return false;

    // check if permissions is an array
    if (!Array.isArray(permissions)) permissions = [permissions]

    permissions.forEach(key => {
        let permitted;
        switch (key) {
            case 'superuser':
                if (!authStore.isSuperUser) allowByPermission = false;
                break;

            default:
                if (!authStore.isSuperUser && !authStore.isOwner) {
                    let keys = key.split('||');
                    if (keys.length > 1) {
                        permitted = false;
                        keys.forEach(key => {
                            if (permitted === true) return
                            permitted = !! authStore.rights.find(right => right.key === key && right.permission[type])
                        })
                    } else {
                        permitted = !! authStore.rights.find(right => right.key === key && right.permission[type])
                    }

                    if (!permitted) allowByPermission = false;
                }
                break;
        }
    });

    return allowByPermission;
}

export const getSettingValues = (settingDefaults, customSettings) => {

    // const settingDefaults = _.get(state.form, 'config.template.settings') || []
    const blocks = settingDefaults.map(el => el.blocks).flat().filter(Boolean) || []
    const groups = settingDefaults.map(el => el.groups).flat().filter(Boolean) || []

    const fields = [
        ...groups.map(group => group.fields).flat().filter(Boolean),
        ...blocks.map(block => block.groups.map(group => group.fields)).flat(2).filter(Boolean),
    ]

    let settingValues = {}
    let options = {}

    // form settings
    // const customSettings = _.get(state.form, 'settings') || []

    // select fields
    // get default values
    // so that invalid values can be determined
    if (fields.length) {
        fields.forEach(setting => {
            if (setting.options && setting.options.length && setting.type !== 'colorpicker') {
                // const option = setting.options.find(option => option.default === true)
                setting.options.forEach(option => {
                    if (!settingValues[setting.key] && ['checkbox', 'multi-select', 'tags'].includes(setting.type)) {
                        settingValues[setting.key] = []
                    }

                    if (['multi-select', 'tags'].includes(setting.type)) {
                        if (['true', '1', 'yes', true].includes(option.default) && !customSettings.find(el => el.key === setting.key && el.options != null)) {
                            if (!settingValues[setting.key]) settingValues[setting.key] = []
                            settingValues[setting.key].push({ key: option.key, value: option.value })
                        }
                    } else {
                        if (['true', '1', 'yes', true].includes(option.default) && !customSettings.find(el => el.key === setting.key && el.value !== undefined)) {
                            if (['checkbox'].includes(setting.type)) {
                                if (!settingValues[setting.key]) settingValues[setting.key] = []
                                settingValues[setting.key].push(option.key)
                            } else {
                                settingValues[setting.key] = option.key
                            }
                        }
                    }

                    _.set(options, `${setting.key}.${option.key}`, option.value)
                })
            } else {
                switch (setting.type) {
                    case 'radio':
                    case 'checkbox':
                    case 'switch':
                    case 'inline-switch':
                        settingValues[setting.key] = false
                        if (setting.value) {
                            settingValues[setting.key] = ['true', '1', 'yes', true].includes(setting.value)
                        } else if (setting.default) {
                            settingValues[setting.key] = ['true', '1', 'yes', true].includes(setting.default)
                        }
                        break;

                    default:
                        settingValues[setting.key] = setting.value || setting.default
                }
            }
        })
    }

    // override with actual values
    if (customSettings.length) {
        customSettings.forEach(setting => {
            // check if selected option exists

            // get related configuration (defined in temaplte JSON)
            const config = fields.find(el => el.key === setting.key)
            const type = _.get(config, 'type')

            switch (type) {
                case 'select':
                    if (setting.key && setting.value != null && _.get(options, `${setting.key}.${setting.value}`) !== undefined) {
                        settingValues[setting.key] = setting.value
                    } else if (setting.value != null && setting.value.trim().split(" ").filter(Boolean).length) {
                        settingValues[setting.key] = setting.value.trim().split(" ").filter(Boolean)
                    }
                    break;

                case 'multi-select':
                case 'tags':
                    if (setting.key && setting.options != null) {
                        settingValues[setting.key] = []
                        setting.options.forEach(el => {
                            if (type === 'tags') {
                                if (!settingValues[setting.key].some(v => v.key === el.key)) {
                                    settingValues[setting.key].push(el)
                                }
                            } else if (type === 'multi-select') {
                                const value = _.get(options, `${setting.key}.${el.key}`)
                                if (value !== undefined && !settingValues[setting.key].some(v => v.key === el.key)) {
                                    settingValues[setting.key].push({ key: el.key, value })
                                }
                            }
                        })
                    } else if (setting.value != null && setting.value.trim().split(" ").filter(Boolean).length) {
                        settingValues[setting.key] = setting.value.trim().split(" ").filter(Boolean)
                    }
                    break;

                case 'checkbox':
                    if (setting.value != null && setting.value.length && setting.value.trim().split(" ").filter(Boolean).length) {
                        settingValues[setting.key] = setting.value.trim().split(" ").filter(Boolean)
                        settingValues[setting.key] = settingValues[setting.key].filter(v => _.get(options, `${setting.key}.${v}`) !== undefined)
                    } else if (settingValues[setting.key] === undefined) {
                        settingValues[setting.key] = []
                    }
                    break;

                // case 'radio':
                case 'switch':
                case 'inline-switch':
                    if (setting.value !== null) {
                        settingValues[setting.key] = ['true', '1', 'yes', true].includes(setting.value)
                    } else if (settingValues[setting.key] === undefined) {
                        settingValues[setting.key] = false
                    }
                    break;

                default:
                    if (setting.value !== undefined) {
                        settingValues[setting.key] = setting.value
                    }
            }
        })
    }

    return settingValues
}

export const addContentToSection = (section, config, content = {}) => {
    let value = {
        id: _.get(content, 'id', uniqid('new:')),
        key: _.get(content, 'key', config.key),
        media: _.get(content, 'media', []),
        files: _.get(content, 'files', []),
        images: _.get(content, 'images', []),
        categories: _.get(content, 'categories', []),
        hyperlink: _.get(content, 'hyperlink', {}),
        position: _.get(content, 'position')
    };

    if (content.position == null) {
        const contents = _.get(section, 'contents') || []
        value.position = Math.max(...contents.map(o => o.position), 0) + 1
    }

    let fields = _.get(config, 'fields') || [];
    let key, settings, setting, options, option, hyperlink;
    fields.forEach(field => {
        key = _.get(field, 'key');
        switch (key) {
            case 'hyperlink':
                let type = 'internal';

                settings = config.settings || [];
                setting = settings.find(setting => setting.key === 'link_types');
                options = _.get(setting, 'options') || [];
                if (options.length) {
                    option = find(options, 'default');
                    if (option) type = option.key;
                }

                hyperlink = {
                    name: '',
                    href: '',
                    type,
                    target: '_self',
                    download: false,
                    menuItem: {}
                };
                value.hyperlink = hyperlink;
                break;

            case 'color':
                if (!field.value && field.options && field.options.length) {
                    option = field.options.find(el => ['true', '1', 'yes', true].includes(el.default));
                    if (option && option.value) value[key] = option.value;
                }
                break;

            default:
                if (key) value[key] = _.get(field, 'value', '');
        }
    });

    // const index = _.get(section, 'contents.length', 0);
    // Vue.set(state.form.menuItems[i].page.sections[s].contents, index, value);

    if (!section.contents) {
        // section.contents = []
        Vue.set(section, 'contents', [])
    }

    // section.contents.push(value)
    Vue.set(section.contents, section.contents.length, value)

    return section
}

export const getFitStringFromProcessObject = process => {
    // let fit = 'fit=crop&crop=focalpoint&x=.5&y=.5&z=1'
    // `rot`/`flip` are included so a deferred rotate/mirror previews immediately —
    // FileController applies both (it only falls back to the image's STORED orientation
    // when the query omits them, which pre-save wouldn't reflect the unsaved edit).
    return Object.entries(process)
        .filter(([key, value]) => ['w', 'h', 'x', 'y', 'z', 'fit', 'crop', 'rot', 'flip'].includes(key) && value != null && value != '')
        .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
        .join('&');
}

export const sortContents = (contents) => {
    contents = contents.sort((a, b) => _.get(a, 'position', 0) - _.get(b, 'position', 0));
    contents.forEach((content, index) => {
        const c = _.findIndex(contents, { id: content.id });
        if (c >= 0) Vue.set(contents[c], 'position', index + 1);
    });
    return contents
}


let _refreshInFlight = null;

// NEVER short-circuit this on a refresh in flight. TokenRefreshLink queues
// every operation that arrives while it is fetching and replays them with the
// fresh token; answering "valid" here instead sends them out with the EXPIRED
// token, the server runs them as a guest and answers 200 with null data. That
// was the empty page on the first route change every morning.
export const isTokenValidOrUndefined = () => {
    const token = Auth.getToken();

    if (! token) return true;

    const jwt = jwtDecode(token);

    if (jwt && jwt.exp) {
        const expirationDate = fromUnixTime(jwt.exp)
        const expired = isPast(expirationDate)

        return expired !== true;
    }
}

export const isRefreshInFlight = () => {
    return !!_refreshInFlight;
}

export const waitForRefreshInFlight = () => {
    return _refreshInFlight || Promise.resolve();
}

export const fetchAccessToken = async () => {
    // If a refresh is already in-flight, wait for it instead of starting another one
    if (_refreshInFlight) {
        await _refreshInFlight;
        return Auth.getToken();
    }

    const authStore = useAuthStore();

    _refreshInFlight = authStore.refresh()
        .then(() => {
            _refreshInFlight = null;
            return Auth.getToken();
        })
        .catch((error) => {
            _refreshInFlight = null;
            throw error;
        });

    return _refreshInFlight;
}

// A token for a request that does not go through Apollo and its refresh link:
// a PDF opened in a new tab, a download, an iframe. Apollo refreshes an expired
// token on its own; nothing else does, so such a request would carry the stale
// one and get a 401. Joins a refresh already in flight instead of starting a
// second one.
export const ensureFreshToken = async () => {
    if (isRefreshInFlight()) {
        await waitForRefreshInFlight();
        return Auth.getToken();
    }

    if (isTokenValidOrUndefined()) return Auth.getToken();

    return fetchAccessToken();
}

// Open a token-authenticated URL in a new tab. The tab is opened synchronously,
// inside the click, so the popup blocker lets it through; the URL is set once
// the token is known to be fresh.
export const openWithFreshToken = async (buildUrl) => {
    const opened = window.open('', '_blank');

    try {
        const url = buildUrl(await ensureFreshToken());
        if (opened) {
            opened.location = url;
        } else {
            window.open(url, '_blank');
        }
    } catch (error) {
        if (opened) opened.close();
        throw error;
    }
}

export function isObject(obj) {
    return typeof obj === 'object' && !Array.isArray(obj) && obj !== null
}

export function isNumeric(str) {
    return !isNaN(str) && !isNaN(parseFloat(str))
}

export function isArray(arr) {
    return Array.isArray(arr)
}

export function splitPath(str) {
    const regex = /([\w\s-]+)|\[([^\]]+)\]/g
    const result = []
    let path

    while ((path = regex.exec(str || ''))) {
        if (str[path.index] === '[') {
            result.push(path[2])
        } else {
            result.push(path[1])
        }
    }

    return result
}

export function getByPath(obj, path) {
  const parts = isArray(path) ? path : splitPath(path)
  const length = parts.length

  for (let i = 0; i < length; i++) {
    if (typeof obj[parts[i]] === 'undefined') {
      return undefined
    }
    obj = obj[parts[i]]
  }

  return obj
}

export const getActiveBreakpoint = (indexes, configs, breakpoint) => {
    let result

    if (configs && configs.length && _.isArray(configs)) {
        _.cloneDeep(configs).sort((a, b) => indexes.indexOf(b.key) - indexes.indexOf(a.key))
            .forEach(config => {
                if (result || indexes.indexOf(config.key) < 0
                    || indexes.indexOf(breakpoint) < indexes.indexOf(config.key)) return

                result = config.key
            })
    }

    return result
}

export const getActiveGridConfig = (indexes, configs, breakpoint, debug = false) => {
    let result

    const activeBreakpoint = getActiveBreakpoint(indexes, configs, breakpoint)

    if (configs && configs.length && _.isArray(configs)) {
        _.cloneDeep(configs).sort((a, b) => indexes.indexOf(b.key) - indexes.indexOf(a.key))
            .forEach(config => {
                // if (result || indexes.indexOf(config.key) < 0
                //     || indexes.indexOf(breakpoint) < indexes.indexOf(config.key)) return

                if (result || config.key !== activeBreakpoint) return

                let options
                if (config.options && _.isArray(config.options) && config.options.length) {
                    options = config.options.map(el => el.value)
                } else if (config.value) {
                    let value = config.value
                    if (typeof value !== 'string' && value.toString !== undefined) value = value.toString()
                    const regex = new RegExp(/[^\s()]+(?:\([^\s()]+(?:\([^()]+\))?(?:, *[^\s()]+(?:\([^()]+\))?)*\))?/, 'g')
                    options = value.match(regex)
                }

                if (options && _.isArray(options)) {
                    if (! result) result = []

                    options.forEach((value, i) => {
                        const matches = /^repeat\((?<count>\d+), (?<track>\w+)\) ?$/.exec(value)
                        if (matches) {
                            const { groups: { count, track }} = matches
                            for (let i = 0; i < count; i++) result.push(track)
                        } else {
                            result.push(value)
                        }
                    })
                }
            })
    } else {
        return configs
    }

    return result
}



// ---



// https://github.com/kouts/vue-set-path
export const vueSet = (obj, pathStr, value) => {
    const path = splitPath(pathStr)
    const length = path.length
    const lastIndex = length - 1

    for (let index = 0; index < length; index++) {
        const prop = path[index]

        // If we are not on the last index
        // we start building the data object from the path
        if (index !== lastIndex) {
            const objValue = obj[prop]

            // If objValue exists, is not primitive and is not observable, then make it so using Vue.set
            if (objValue && typeof objValue === 'object') {
                // eslint-disable-next-line no-prototype-builtins
                if (!objValue.hasOwnProperty('__ob__')) {
                    Vue.set(obj, prop, objValue)
                }
                // Array to object transformation
                // Check if parent path is an array, we are not on the last item
                // and the next key in the path is not a number
                if (isArray(objValue) && !isNumeric(path[index + 1])) {
                    Vue.set(obj, prop, {})
                }
            } else {
                // Create an empty object or an empty array based on the next path entry
                if (isNumeric(path[index + 1])) {
                    Vue.set(obj, prop, [])
                } else {
                    Vue.set(obj, prop, {})
                }
            }
        } else {
            // If we are on the last index then we just assign the the value to the data object
            // Note: If we used obj[prop] = value; arrays wouldn't be updated.
            Vue.set(obj, prop, value)
        }
        obj = obj[prop]
    }
}

export const set = vueSet

// https://github.com/kouts/vue-set-path
export const vueSetMany = (obj, path, value) => {
    if (typeof path === 'string') {
        vueSet(obj, path, value)
    } else if (isObject(path)) {
        for (const key in path) {
            vueSet(obj, key, path[key])
        }
    } else {
        throw Error('Arguments must be either string or object.')
    }
}

// https://github.com/kouts/vue-set-path
export const vueDelete = (obj, pathStr) => {
    const path = splitPath(pathStr)
    const prop = path.pop()

    Vue.delete(getByPath(obj, path), prop)
}

export const unset = vueDelete

// https://github.com/kouts/vue-set-path
export const vueDeleteMany = (obj, path) => {
    if (typeof path === 'string') {
        vueDelete(obj, path)
    } else if (isArray(path)) {
        path.forEach((item) => {
            vueDelete(obj, item)
        })
    } else {
        throw Error('Arguments must be either string or array.')
    }
}

export const reorderItemsByPosition = (items, itemId, newPosition) => {
    const position = parseInt(newPosition);
    if (isNaN(position) || position < 1) return false;

    const targetItem = items.find(item => parseInt(item.id) === parseInt(itemId));
    if (!targetItem) return false;
    if (!targetItem.pivot) targetItem.pivot = { position: 0 };
    const oldPosition = targetItem.pivot.position;

    if (oldPosition === position) return false;

    const movingDown = position > oldPosition;

    items.forEach(item => {
        if (!item.pivot) item.pivot = { position: 0 };
        if (parseInt(item.id) === parseInt(itemId)) return;

        if (movingDown && item.pivot.position > oldPosition && item.pivot.position <= position) {
            item.pivot.position = item.pivot.position - 1;
        } else if (!movingDown && item.pivot.position >= position && item.pivot.position < oldPosition) {
            item.pivot.position = item.pivot.position + 1;
        }
    });

    targetItem.pivot.position = position;
    return true;
}

